<template>
  <div class="min-h-screen px-6 py-8 md:px-10">
    <div class="w-full space-y-6">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 class="mt-2 text-3xl text-gray-900">Relatório chats finalizados</h1>
          <p class="mt-2 max-w-2xl text-sm text-gray-500">Consulte os chats finalizados por período e operador.</p>
        </div>
      </div>

      <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div class="p-6">
          <form @submit.prevent="start">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-gray-500">De</label>
                <input v-model="form.de" type="date" class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100" />
              </div>

              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-gray-500">Até</label>
                <input v-model="form.ate" type="date" class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100" />
              </div>

              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-gray-500">Operador</label>

                <div class="relative group">
                  <select
                    v-model="form.operador"
                    id="operador-select"
                    class="h-11 w-full cursor-pointer appearance-none rounded-xl border border-gray-200 bg-white px-4 pr-10 text-gray-700 shadow-sm transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
                  >
                    <option :value="null">Todos</option>

                    <option v-for="operador in operadores" :key="operador._id" :value="operador._id">
                      {{ operador.nome }}
                    </option>
                  </select>

                  <svg class="select-icon h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              <div class="flex items-end">
                <button
                  type="submit"
                  class="h-11 w-full rounded-xl font-medium text-white transition"
                  :style="{ backgroundColor: primaryColor }"
                  @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
                  @mouseleave="$event.target.style.backgroundColor = primaryColor"
                >
                  Buscar
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div class="max-w-2xl rounded-2xl px-5 py-4 shadow-sm" :style="{ backgroundColor: getPrimaryBackground(0.15), borderColor: primaryColor, borderWidth: '1px', color: primaryColor }">
        <p class="text-sm font-medium">
          Essa tabela mostra <span class="font-bold">APENAS</span> as conversas finalizadas pelo botão <span class="font-bold">"Finalizar conversas"</span>.
        </p>
      </div>

      <div class="w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div class="overflow-x-auto">
          <table class="w-full min-w-max text-sm">
            <thead class="border-b bg-gray-100 tracking-wider text-gray-600">
              <tr>
                <th class="px-6 py-4 text-left font-medium">Nome</th>
                <th class="px-6 py-4 text-left font-medium">Email</th>
                <th class="px-6 py-4 text-center font-medium">Data</th>
                <th class="px-6 py-4 text-center font-medium">Quantidade de conversas encerradas</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(log, index) in logsDosChats" :key="index" class="border-b border-gray-100 transition hover:bg-slate-50">
                <td class="px-6 py-5">
                  <div class="flex items-center gap-4">
                    <div class="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-sm font-semibold text-blue-700">
                      {{ (log.nome || "").slice(0, 2).toUpperCase() }}
                    </div>

                    <div class="flex flex-col">
                      <span class="font-medium text-gray-800">
                        {{ log.nome || "-" }}
                      </span>
                      <span class="text-xs text-gray-400"> Operador </span>
                    </div>
                  </div>
                </td>

                <td class="px-6 py-5 font-medium text-gray-700">
                  {{ log.email || "-" }}
                </td>

                <td class="px-6 py-5 text-center font-medium text-gray-700">
                  {{ log.data || "-" }}
                </td>

                <td class="px-6 py-5 text-center">
                  <span class="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700">
                    {{ log.quantidade || 0 }}
                  </span>
                </td>
              </tr>

              <tr v-if="logsDosChats.length === 0">
                <td colspan="4" class="px-6 py-12 text-center text-sm text-gray-400">Nenhum resultado encontrado com os filtros atuais.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { colorMixin } from "@/utils/colorMixin";

export default {
  mixins: [colorMixin],
  data() {
    return {
      breadcrumb: [{ url: "/relatorios/relatorio-chats-finalizados", value: "Relatório chats finalizados" }],
      logsDosChats: [],
      operadores: [],
      form: {
        de: moment().startOf("M").format("YYYY-MM-DD"),
        ate: moment().endOf("M").format("YYYY-MM-DD"),
        operador: null
      }
    };
  },
  methods: {
    async start() {
      const de = moment(this.form.de).format("DD/MM/YYYY");
      const ate = moment(this.form.ate).format("DD/MM/YYYY");

      const params = new URLSearchParams({
        de,
        ate,
        operador: this.form.operador ? String(this.form.operador) : ""
      });

      try {
        const response = await this.$http.get(`/v1/chatv2/conversas-encerradas?${params.toString()}`);

        if (!response.data || !response.data.success) {
          this.logsDosChats = [];
          return this.$vToastify.error((response.data && response.data.err) || "Não foi possível carregar o relatório de chats finalizados.");
        }

        this.logsDosChats = response.data.logsDeConversas || [];
      } catch (error) {
        this.logsDosChats = [];
        const errMessage = (error.response && error.response.data && error.response.data.err) || error.message || "Falha ao consultar o backend do relatório de chats finalizados.";
        this.$vToastify.error(errMessage);
      }
    },

    async buscaOperadores() {
      try {
        const operadoresReq = await this.$http.post("/v1/operador/list", {
          all: true
        });
        
        if (operadoresReq.data && operadoresReq.data.data && Array.isArray(operadoresReq.data.data)) {
          this.operadores = operadoresReq.data.data;
        } else if (operadoresReq.data && Array.isArray(operadoresReq.data)) {
          this.operadores = operadoresReq.data;
        } else {
          console.warn("Resposta inesperada de operadores:", operadoresReq.data);
          this.operadores = [];
        }
      } catch (error) {
        console.error("Erro ao buscar operadores:", error);
        this.$vToastify.error("Erro ao carregar lista de operadores");
        this.operadores = [];
      }
    }
  },

  async beforeMount() {
    await this.start();
    await this.buscaOperadores();
  }
};
</script>

<style scoped>
.select-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  pointer-events: none;
  color: #9ca3af;
  transform: translateY(-50%);
  transition: transform 0.2s ease, color 0.2s ease;
}

#operador-select::-ms-expand {
  display: none;
}

#operador-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: none !important;
}

.group:focus-within .select-icon {
  color: #6366f1;
  transform: translateY(-50%) rotate(180deg);
}
</style>
